import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@mlm/ui';
import type { ThemeTokens } from '@mlm/shared';
import {
  getTenantTheme,
  patchTenantTheme,
  tenantLogoUrl,
  uploadTenantLogo,
} from '../lib/api';

type LogoWhich = 'light' | 'dark' | 'favicon';

const KEY_FIELD: Record<LogoWhich, 'logoKey' | 'logoKeyDark' | 'faviconKey'> = {
  light: 'logoKey',
  dark: 'logoKeyDark',
  favicon: 'faviconKey',
};
// Favicon has no dedicated URL token — it falls back to logoKey → logoUrl.
const URL_FIELD: Record<LogoWhich, 'logoUrl' | 'logoUrlDark' | null> = {
  light: 'logoUrl',
  dark: 'logoUrlDark',
  favicon: null,
};

/**
 * A single brand-asset control: image preview + upload + "or paste a URL"
 * fallback + clear. Uploads ALWAYS persist immediately (courier-logo precedent —
 * the returned merged theme is handed back via `onUploaded`). URL edits and
 * clearing the uploaded key are delegated to the host via `onFieldChange`, so
 * the Theme editor can fold them into its batch draft while the Storefront Brand
 * tab persists them right away.
 */
export function LogoUploadField({
  which,
  label,
  hint,
  theme,
  onUploaded,
  onFieldChange,
  disabled,
}: {
  which: LogoWhich;
  label: string;
  hint?: string;
  theme: ThemeTokens;
  onUploaded: (mergedTheme: ThemeTokens) => void;
  onFieldChange: (patch: Partial<ThemeTokens>) => void;
  disabled?: boolean;
}) {
  const keyField = KEY_FIELD[which];
  const urlField = URL_FIELD[which];
  const currentKey = theme[keyField];
  const currentUrl = urlField ? theme[urlField] ?? '' : '';

  const [local, setLocal] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [urlDraft, setUrlDraft] = useState(currentUrl);
  useEffect(() => setUrlDraft(currentUrl), [currentUrl]);

  // Preview precedence: in-flight object URL → uploaded key (cache-busted by the
  // key value so a re-upload to the same /tenant/logo/:which URL refreshes) →
  // pasted URL → empty.
  const src = local
    ? local
    : currentKey
      ? `${tenantLogoUrl(which)}?v=${encodeURIComponent(currentKey)}`
      : currentUrl || null;

  async function pick(file: File) {
    setLocal(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { theme: merged } = await uploadTenantLogo(file, which);
      onUploaded(merged);
    } finally {
      setUploading(false);
      setLocal(null);
    }
  }

  function commitUrl() {
    const next = urlDraft.trim();
    if (next === (currentUrl ?? '')) return;
    onFieldChange({ [urlField as string]: next });
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium">{label}</div>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      <div className="flex items-center gap-3">
        {src ? (
          <img
            src={src}
            alt=""
            className="h-12 w-20 rounded border bg-white object-contain p-1"
          />
        ) : (
          <div className="h-12 w-20 rounded border bg-muted" />
        )}
        <input
          type="file"
          accept="image/png,image/jpeg"
          className="text-xs"
          disabled={disabled || uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void pick(f);
          }}
        />
        {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
        {currentKey && !uploading && (
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-destructive"
            disabled={disabled}
            onClick={() => onFieldChange({ [keyField]: '' })}
          >
            Remove
          </button>
        )}
      </div>
      {urlField && (
        <Input
          className="h-8 font-mono text-xs"
          placeholder="or paste a URL — https://cdn…/logo.png"
          value={urlDraft}
          disabled={disabled}
          onChange={(e) => setUrlDraft(e.target.value)}
          onBlur={commitUrl}
        />
      )}
    </div>
  );
}

/**
 * Self-contained brand-logos card for the Storefront → Brand tab. Owns the
 * `['tenant-theme']` copy and persists every change immediately (uploads persist
 * server-side; URL/clear go through `patchTenantTheme`) — no entanglement with
 * the storefront draft, so the layer separation (Theme = tokens, Storefront =
 * copy) stays honest while the control is still reachable from the Brand tab.
 */
export function TenantLogosCard({ disabled }: { disabled?: boolean }) {
  const qc = useQueryClient();
  const themeQ = useQuery({ queryKey: ['tenant-theme'], queryFn: getTenantTheme });
  const [theme, setTheme] = useState<ThemeTokens | null>(null);
  useEffect(() => {
    if (themeQ.data && !theme) setTheme(themeQ.data);
  }, [themeQ.data, theme]);

  const refresh = (merged: ThemeTokens) => {
    setTheme(merged);
    qc.invalidateQueries({ queryKey: ['tenant-theme'] });
    qc.invalidateQueries({ queryKey: ['tenant-config'] });
  };
  const persist = useMutation({
    mutationFn: (patch: Partial<ThemeTokens>) => patchTenantTheme(patch),
    onSuccess: refresh,
  });

  if (!theme) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const field = (which: LogoWhich, label: string, hint?: string) => (
    <LogoUploadField
      which={which}
      label={label}
      hint={hint}
      theme={theme}
      disabled={disabled || persist.isPending}
      onUploaded={refresh}
      onFieldChange={(patch) => persist.mutate(patch)}
    />
  );

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {field('light', 'Logo', 'Shown in the storefront header and house-store badges. PNG or JPEG.')}
      {field('dark', 'Logo — dark surfaces (optional)', 'A variant used on dark backgrounds (e.g. the footer).')}
      {field('favicon', 'Favicon (optional)', 'The browser-tab icon. Falls back to the logo when unset.')}
      <p className="text-[11px] text-muted-foreground sm:col-span-2">
        Logos are stored with the theme — colors and fonts live under Layout &amp; Theme. Uploads
        save immediately.
      </p>
    </div>
  );
}
